import { noop } from 'lodash/fp';
import classnames from 'classnames/bind';
import type { ComponentProps } from 'react';
import { useTheme } from 'app/components/core/ThemeProvider';
import { useDefaultId } from 'app/lib/hooks';
import * as styles from './ToggleSwitch.css';

const cx = classnames.bind(styles);

export interface Props extends Omit<ComponentProps<'div'>, 'onChange'> {
    testId?: string;
    label?: string;
    name?: string;
    value?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    bolder?: boolean;
    reverse?: boolean;
    onChange: (value: boolean) => void;
}

export function ToggleSwitch({
    testId,
    className,
    id,
    label,
    name,
    value = false,
    disabled = false,
    fullWidth = false,
    bolder = false,
    reverse = false,
    onChange = noop,
    ...props
}: Props) {
    const myId = useDefaultId('toggle-switch', id);
    const { themeClassname } = useTheme();

    const labelId = `${myId}-label`;
    const classes = cx('base', { fullWidth, bolder, reverse }, themeClassname, className);
    const handleClick = () => onChange(!value);

    return (
        <div data-testid={testId} className={classes} onClick={handleClick} {...props}>
            {label && (
                <span id={labelId} className={styles.label}>
                    {label}
                </span>
            )}
            <button
                type="button"
                role="switch"
                className={styles.switch}
                name={name}
                disabled={disabled}
                aria-labelledby={label ? labelId : undefined}
                aria-checked={Boolean(value)}
            />
        </div>
    );
}
